import camelcaseKeys from 'camelcase-keys'
import { create } from 'zustand'
import { supabase } from '../supabaseClient'
import type { ProvisionedTask } from './useTasks'
import {
  isMissingRpc,
  parseNodeTaskExtras,
  type CatalogueClass,
  type NodeTaskExtra,
} from '../utilities/computeNodePage'

/**
 * Every task one node has run, for the compute node page (nobgp/app#330).
 *
 * ⚠ NOT kept live by realtime: `tasks` is not in the `supabase_realtime`
 * publication (see `useTasks`). The node page re-reads on open, on a timer while
 * a task runs, and after Extend or Terminate.
 *
 * ⚠ Loaded on demand and reset on sign-out (`controller.signOut`), like
 * `useTasks`.
 *
 * ⚠⚠ A RE-ADOPTED TASK MAY CARRY NO `node_id` (nobgp-router#1065). The fix is
 * merged and ships in the next router release. Until that release, a node
 * re-provisioned by name shows only the tasks that do carry it.
 * The list reads `tasks.node_id` and nothing else, so it cannot attach a task to
 * the wrong node by name.
 *
 * ⚠ The "node has an open task" test is derived HERE, locally, from these rows.
 * nobgp/app#333 (for #329) adds `useComputeNodes` and `openComputeByNode` for the
 * graph badge; once both are merged, the node page's open-task test should read
 * that hook instead of this derivation.
 */

/**
 * The `get_node_tasks` row (nobgp-router#1466, router PR #1472,
 * migration 0191). Not in `supabase.d.ts` yet: regenerate the types with
 * `npm run types` after the migration applies, then replace this narrow type and
 * the untyped call below.
 */
export type NodeTasksRpcRow = {
  task_id: string
  class: string | null
  cpu: number
  memory_mb: number
  price_cents_per_hour: number | null
  created_at: string
  completed_at: string | null
  deadline_at: string | null
  billable: boolean
  billed_minutes: number
  cost_cents: number | null
  stop_reason: string | null
}

type UntypedRpc = (
  fn: string,
  args: Record<string, unknown>,
) => PromiseLike<{ data: unknown; error: { code?: string; message: string } | null; status: number }>

export interface NodeTasksState {
  nodeId: string | null
  tasks: ProvisionedTask[]
  catalogue: CatalogueClass[]
  /**
   * Cost and stop reason per task, or NULL when `get_node_tasks` is not deployed.
   *
   * ⚠ NULL is a state, not an empty map: an empty map means the RPC answered and
   * knows no task, so every cost is unpriced; null means the columns are left out.
   */
  extras: Map<string, NodeTaskExtra> | null
  /**
   * True when `get_node_tasks` is deployed and the read failed.
   *
   * ⚠ `extras` is null in that case too, but the page must not say "not available yet".
   */
  extrasError: boolean
  loading: boolean
  error: string | null
  get: (nodeId: string) => Promise<void>
  /** Read the compute catalogue only, for the New compute node dialog. */
  getCatalogue: () => Promise<void>
  reset: () => void
}

const state = {
  nodeId: null as string | null,
  tasks: [] as ProvisionedTask[],
  catalogue: [] as CatalogueClass[],
  extras: null as Map<string, NodeTaskExtra> | null,
  extrasError: false,
  loading: false,
  error: null as string | null,
}

type RawTask = {
  id: string
  nodeId: string | null
  nodeName: string
  networkId: string
  cpu: number | null
  memoryMb: number | null
  createdAt: string
  completedAt: string | null
  deadlineAt: string | null
}

// ⚠ Counts every `get`. Two reads of ONE node can be out at once (the poll and the re-read after
// Extend), and they land in completion order. An older read that lands last would put the
// deadline from before the Extend back on the screen. Comparing `nodeId` does not catch that.
let readSeq = 0

const useNodeTasks = create<NodeTasksState>()((set, get) => ({
  ...state,
  get: async (nodeId: string) => {
    // A different node: drop the previous node's rows at once, so its tasks never
    // render under this node's name while the read is out.
    if (get().nodeId !== nodeId) set({ ...state, nodeId })
    set({ loading: true })
    const seq = ++readSeq

    const rpc = supabase.rpc as unknown as UntypedRpc
    const [taskRes, classRes, rpcRes] = await Promise.all([
      supabase
        .from('tasks')
        .select('id, node_id, node_name, network_id, cpu, memory_mb, created_at, completed_at, deadline_at')
        .eq('node_id', nodeId)
        .order('created_at', { ascending: false }),
      supabase.from('compute_classes').select('name, cpu, memory_mb, price_cents_per_hour, offered'),
      rpc.call(supabase, 'get_node_tasks', { p_node_id: nodeId }),
    ])

    // ⚠ The reader may have opened another node while these were out, or a newer read was started.
    if (seq !== readSeq || get().nodeId !== nodeId) return

    if (classRes.error) console.warn(classRes.error)

    let extras: Map<string, NodeTaskExtra> | null = null
    let extrasError = false
    if (!rpcRes.error) extras = parseNodeTaskExtras(rpcRes.data)
    else if (!isMissingRpc(rpcRes.error, rpcRes.status)) {
      console.warn('get_node_tasks failed, showing tasks without cost', rpcRes.error)
      extrasError = true
    }

    if (taskRes.error) {
      console.warn(taskRes.error)
      return set({ loading: false, error: taskRes.error.message })
    }

    const tasks: ProvisionedTask[] = (camelcaseKeys(taskRes.data ?? []) as unknown as RawTask[]).map(row => ({
      ...row,
      // The node page supplies the live name; these rows are only read for their times and shape.
      nodeReadable: true,
      nodeDeleted: false,
      // Not read here: the node page gates its controls on the node's tenant directly
      // (`NodeComputeSection`'s `tenantOrgId`), not per task.
      nodeOwnerOrgId: null,
      networkName: '',
      // The node page is open, so its network is one of the active org's.
      networkInOrg: true,
    }))

    set({
      tasks,
      catalogue: camelcaseKeys(classRes.data ?? []) as CatalogueClass[],
      extras,
      extrasError,
      loading: false,
      error: null,
    })
  },
  getCatalogue: async () => {
    const { data, error } = await supabase
      .from('compute_classes')
      .select('name, cpu, memory_mb, price_cents_per_hour, offered')
    if (error) {
      console.warn(error)
      return
    }
    set({ catalogue: camelcaseKeys(data ?? []) as CatalogueClass[] })
  },
  // ⚠ Keeps nothing: the previous user's node and task rows must not survive a sign-in in the same tab.
  reset: () => set({ ...state }),
}))

export default useNodeTasks
